import { useEffect, useId, useRef, useState } from "react";

import { useIsMutating, useQueryClient } from "@tanstack/react-query";

import { EToySessionState, type TActuatorCommand, type TActuatorValue, type TGetToysSessionResponse } from "@api/toys";

import { useToysStore } from "@storage/global/useToysStore";
import { usePlayToysMutation, useStopToysMutation } from "@storage/queries/toys";

import { QUERY_KEYS } from "@configs/api";
import { actuatorIDMap } from "@configs/toys";

import { useToysSession } from "./useToysSession";

export const useToysControls = () => {
	const queryClient = useQueryClient();
	const [isGuideOpen, setIsGuideOpen] = useState(false);
	const [loadedSessionId, setLoadedSessionId] = useState<string | null>(null);

	const guideId = useId();
	const contentRef = useRef<HTMLDivElement>(null);
	const {
		activePrivateShowId,
		isToyContainerExpanded: isOpen,
		setIsToyContainerExpanded: setIsOpen,
		isOpenToyContainer,
		providerName,
		setIsToyContainerDisabled,
	} = useToysStore();
	const { session, capabilities, sessionToken, isFetching, isError } = useToysSession();
	const { mutate: playToys } = usePlayToysMutation(sessionToken);
	const { mutate: stopToys } = useStopToysMutation(sessionToken);
	const sessionKey = [QUERY_KEYS.TOYS_SESSION, activePrivateShowId];
	const isMutationPending = useIsMutating({ mutationKey: sessionKey }) > 0;

	const hasSessionData = Boolean(sessionToken && session && capabilities);
	const isLoading = !isError && (!hasSessionData || (loadedSessionId !== session?.session_id && isFetching));
	const sessionState = isError ? EToySessionState.CLOSED : (session?.state ?? EToySessionState.IDLE);
	const isToyContainerDisabled = isLoading || sessionState !== EToySessionState.GRANTED_RUNNING;
	const isActionPending = isMutationPending || isLoading;

	useEffect(() => {
		if (!isOpenToyContainer) {
			setIsGuideOpen(false);
		}
	}, [isOpenToyContainer]);

	useEffect(() => {
		if (!isOpenToyContainer) {
			setLoadedSessionId(null);
		} else if (hasSessionData && !isFetching && !isError && session) {
			setLoadedSessionId(session.session_id);
		}
	}, [hasSessionData, isError, isFetching, isOpenToyContainer, session]);

	const handleToggleGuide = () => {
		contentRef.current?.scrollTo({ top: 0 });
		setIsGuideOpen((isVisible) => !isVisible);
	};

	/**
	 * Get the current session state if it is the expected state for action buttons
	 * Read synchronously: a second click can arrive before React renders the pending state.
	 */
	const getActionSession = (expectedState: EToySessionState) => {
		const currentSession = queryClient.getQueryData<TGetToysSessionResponse>(sessionKey);
		if (
			isLoading ||
			isError ||
			!sessionToken ||
			useToysStore.getState().activePrivateShowId !== activePrivateShowId ||
			queryClient.isMutating({ mutationKey: sessionKey }) > 0 ||
			!currentSession ||
			currentSession.session_id !== session?.session_id ||
			currentSession.state !== expectedState
		) {
			return;
		}

		return currentSession;
	};

	const handleOpenPlay = () => {
		const currentSession = getActionSession(EToySessionState.GRANTED_STOPPED);
		if (!currentSession || !capabilities) {
			return;
		}

		const { actuators, provider } = capabilities.capabilities;
		const defaultActuatorId = actuatorIDMap[provider.toLowerCase()];
		const defaultActuator = defaultActuatorId
			? actuators.find((actuator) => (actuatorIDMap[actuator.id] ?? actuator.id) === defaultActuatorId)
			: actuators[0];

		if (!defaultActuator) {
			return;
		}

		const actuatorId = actuatorIDMap[defaultActuator.id] ?? defaultActuator.id;
		const defaultCommand: TActuatorCommand = Object.fromEntries(
			Object.entries(defaultActuator.params).map(([name, parameter]): [string, TActuatorValue] => [
				name,
				currentSession.motion?.actuators?.[actuatorId]?.[name] ??
					parameter.default ??
					(parameter.kind === "span" ? [parameter.min, parameter.max] : parameter.min),
			]),
		);

		setIsToyContainerDisabled(false);

		contentRef.current?.scrollTo({ top: 0 });
		setIsGuideOpen(false);
		setIsOpen(true);
		playToys({ motion: { actuators: { [actuatorId]: defaultCommand } } });
	};

	const handleStop = () => {
		if (!getActionSession(EToySessionState.GRANTED_RUNNING)) {
			return;
		}

		stopToys();
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			contentRef.current?.scrollTo({ top: 0 });
			setIsGuideOpen(false);
		}
	};

	return {
		isGuideOpen,
		guideId,
		contentRef,
		isOpen,
		isOpenToyContainer,
		providerName,
		session,
		capabilities,
		sessionToken,
		isLoading,
		isError,
		sessionState,
		isToyContainerDisabled,
		isActionPending,
		handleToggleGuide,
		handleOpenPlay,
		handleStop,
		handleOpenChange,
	};
};
